import * as React from 'react';

import { RecordTagColour } from '@gearbelt/global';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

export type BadgeVariant = RecordTagColour | 'default' | 'destructive' | 'success' | 'yellow' | 'grey' | 'outline';

// Colour classes per variant, shared with the multi-select's tag chips so the
// two surfaces cannot drift.
export const badgeVariantClasses: Record<BadgeVariant, string> = {
  default: 'bg-default-light-blue text-default-dark-blue hover:bg-default-light-blue/80',
  destructive: 'bg-default-light-red text-default-dark-red hover:bg-default-light-red/80',
  success: 'bg-default-light-green text-default-dark-green hover:bg-default-light-green/80',
  yellow: 'bg-default-light-yellow text-default-dark-yellow hover:bg-default-light-yellow/80',
  grey: 'bg-grey-1 text-default-text hover:bg-grey-1/80',
  violet: 'bg-default-light-violet text-default-dark-violet hover:bg-default-light-violet/80',
  orange: 'bg-default-light-orange text-default-dark-orange hover:bg-default-light-orange/80',
  sky: 'bg-default-light-sky text-default-dark-sky hover:bg-default-light-sky/80',
  outline: 'text-default-text',
};

const badgeVariants = cva(
  'inline-flex items-center w-fit rounded p-[2px_5px] font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 select-none',
  {
    variants: {
      variant: badgeVariantClasses,
      size: {
        default: 'text-sm',
        sm: 'text-xs',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, size, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant, size }), className)} {...props} />;
}

export { Badge, badgeVariants };
